// ─── Marketplace Continuity, Honest Pricing & Log Privacy V1 ───────────────
// Hotfix: Marketplace Load-More Continuity Completion V1
//
// Pure decision logic behind runMarketplaceSearch's "Ver más" continuity in
// routes/experiences.tsx, extracted so the whole state machine is directly
// unit-testable — same rationale marketplaceSearchParams.ts's own header
// comment gives ("preferred over source-text guardrails since this module
// has zero React/JSX in it").
//
// Why an explicit context object, not loose booleans
// ────────────────────────────────────────────────────
// The previous version of this module tracked "have we already recovered
// once" (isRecoveryAttempt) and "how many times have we polled" (via a
// fixed-length WARMUP_POLL_DELAYS_MS array) as separate opts fields on
// runMarketplaceSearch. That let a real bug slip through: the warmup-poll
// retry it scheduled via setTimeout carried `warmupPollAttempt` forward but
// silently DROPPED `isRecoveryAttempt` — so this exact sequence:
//
//   append -> cursor_expired -> recover (reload page 1) -> append-fresh
//   -> inventory_warming -> poll -> cursor_expired (again)
//
// let the poll's cursor_expired start a SECOND silent recovery, because
// nothing on that call remembered recovery had already been used once.
//
// LoadMoreActionContext fixes this by making the WHOLE action's progress
// (requestId, recoveryCount, warmupPollAttempt, deadlineAt) one value that
// is always threaded forward together, via decideNextLoadMoreStep's own
// `nextContext` on every transition — there is no code path that can carry
// one field forward while forgetting another.

// ── Action context ──────────────────────────────────────────────────────

export interface LoadMoreActionContext {
  /** Logical id of the whole user action (one "Cargar más" click, or the
   *  page-1 fetch a filter/sequence change triggers) — identical across
   *  every step this action spawns (recovery reload, append-fresh retry,
   *  every warmup poll). Compared against searchRequestIdRef.current at
   *  every step, so a genuinely NEW action (its own fresh requestId) can
   *  cancel every pending step of an OLDER one — never the reverse. Never
   *  reassigned once minted; only a brand-new context (a new action) gets
   *  a new requestId. */
  requestId: number;
  /** How many stale-cursor auto-recoveries THIS action has already used.
   *  Capped at MAX_RECOVERY_COUNT_PER_ACTION (1) — once spent, a LATER
   *  stale-cursor failure for the SAME action (the fresh cursor a
   *  recovery itself produced expiring too, or a warmup poll coming back
   *  stale) goes straight to manual-exit, never a second silent recovery. */
  recoveryCount: number;
  /** How many warmup polls have already run against the CURRENT cursor.
   *  Reset to 0 whenever a fresh cursor starts its own polling episode
   *  (after a recovery's append-fresh) — used only to compute the next
   *  backoff delay; whether to KEEP polling is the deadline's job, never
   *  this counter's. */
  warmupPollAttempt: number;
  /** Absolute timestamp (same clock as the `now` passed into
   *  decideNextLoadMoreStep) after which this action stops polling and
   *  exits manually instead. Minted ONCE when the action starts and
   *  carried unchanged through every recovery/poll step — never extended,
   *  never reset by a recovery. */
  deadlineAt: number;
}

/** Exactly one automatic stale-cursor recovery per action — never more,
 *  regardless of how many further failures the same action later hits. */
export const MAX_RECOVERY_COUNT_PER_ACTION = 1;

/** Explicit total wall-clock budget for polling a warming cursor, staging-
 *  sized (Hotfix: Marketplace Load-More Continuity Completion V1 — the
 *  previous ~5.1s fixed schedule was too short for a real staging warmup;
 *  15s is the deliberate, generous replacement). */
export const WARMUP_POLL_TOTAL_BUDGET_MS = 15_000;

export interface ContinuityExperienceItem {
  id?: string | null;
  provider_name?: string | null;
  provider_id?: string | null;
  title?: string | null;
}

function normalizedIdentityPart(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Preserve the visible prefix and append only experiences not seen before.
 *
 * Backend snapshots are authoritative. This is a defensive UI boundary for
 * retries, old deployments, and concurrent responses: a page can never make
 * an already-rendered item disappear or append the same provider product
 * twice. URLs are deliberately not used as identity or exposed here.
 */
export function appendUniqueExperienceItems<T extends ContinuityExperienceItem>(
  existing: readonly T[],
  incoming: readonly T[],
): T[] {
  const merged = [...existing];
  const seenIds = new Set(existing.map((item) => normalizedIdentityPart(item.id)).filter(Boolean));
  const seenProviderProducts = new Set(
    existing
      .map((item) => {
        const provider = normalizedIdentityPart(item.provider_name);
        const product = normalizedIdentityPart(item.provider_id);
        return provider && product ? `${provider}:${product}` : "";
      })
      .filter(Boolean),
  );
  const seenTitles = new Set(
    existing.map((item) => normalizedIdentityPart(item.title)).filter(Boolean),
  );

  for (const item of incoming) {
    const id = normalizedIdentityPart(item.id);
    const provider = normalizedIdentityPart(item.provider_name);
    const product = normalizedIdentityPart(item.provider_id);
    const providerProduct = provider && product ? `${provider}:${product}` : "";
    const title = normalizedIdentityPart(item.title);
    if (
      (id && seenIds.has(id))
      || (providerProduct && seenProviderProducts.has(providerProduct))
      || (title && seenTitles.has(title))
    ) {
      continue;
    }
    merged.push(item);
    if (id) seenIds.add(id);
    if (providerProduct) seenProviderProducts.add(providerProduct);
    if (title) seenTitles.add(title);
  }
  return merged;
}

const WARMUP_POLL_BASE_DELAY_MS = 700;
const WARMUP_POLL_BACKOFF_FACTOR = 1.6;
/** Per-step delay cap — backoff keeps growing "moderately" (per the
 *  product brief) but is never allowed to balloon into a multi-second gap
 *  between polls just because the action has been waiting a while. */
const WARMUP_POLL_MAX_DELAY_MS = 3_000;

/** Mint a brand-new action context — call this exactly once per genuinely
 *  new user action (a fresh "Cargar más" click, or the page-1 fetch a
 *  filter/sequence change triggers). Every subsequent step of THIS action
 *  must reuse (a copy of) the context this returns, never mint another. */
export function createLoadMoreActionContext(requestId: number, now: number): LoadMoreActionContext {
  return {
    requestId,
    recoveryCount: 0,
    warmupPollAttempt: 0,
    deadlineAt: now + WARMUP_POLL_TOTAL_BUDGET_MS,
  };
}

/** Moderate, capped exponential backoff for the delay before the NEXT
 *  warmup poll. Non-decreasing by construction (each attempt's delay is
 *  >= the previous one, until the cap), and always positive. */
export function computeWarmupPollDelayMs(attempt: number): number {
  if (attempt < 0) {
    throw new RangeError(`computeWarmupPollDelayMs: attempt ${attempt} out of range`);
  }
  const raw = WARMUP_POLL_BASE_DELAY_MS * Math.pow(WARMUP_POLL_BACKOFF_FACTOR, attempt);
  return Math.round(Math.min(raw, WARMUP_POLL_MAX_DELAY_MS));
}

/** True while the action's total warmup-poll budget hasn't run out yet.
 *  `now` is injected (never read from Date.now() internally) so this and
 *  everything built on it stays a pure, deterministically-testable
 *  function — no fake timers required. */
export function hasWarmupBudgetRemaining(context: LoadMoreActionContext, now: number): boolean {
  return now < context.deadlineAt;
}

/**
 * True for either backend error code that means "this cursor no longer
 * resolves against the current pool" — cursor_expired (pool mutated,
 * revision bumped) and invalid_cursor (malformed/mismatched search key).
 * Both are recovered the same way: a silent page-1 reload followed by one
 * retry against the fresh cursor it yields.
 */
export function isStaleCursorError(error: string | null | undefined): boolean {
  return error === "cursor_expired" || error === "invalid_cursor";
}

// ── Pure state machine ──────────────────────────────────────────────────
//
// decideNextLoadMoreStep() is the single source of truth for "what happens
// next" at every one of the state machine's decision points. It never
// touches React state, never calls fetch/setTimeout — the caller
// (runMarketplaceSearch in experiences.tsx) is responsible for actually
// carrying out whatever transition comes back.

/** Minimal, already-normalized shape decideNextLoadMoreStep needs from a
 *  real ExperienceSearchResponse — callers extract just these fields so
 *  this module never depends on the full API response type. */
export interface LoadMoreResponseLike {
  success: boolean;
  error?: string | null;
  inventory_warming?: boolean;
  itemsCount?: number;
  hasMore?: boolean;
  nextCursor?: string | null;
}

export type LoadMoreEvent =
  /** The result of a normal (or warmup-poll, or post-recovery) append
   *  ("cargar más") attempt. */
  | { kind: "append_result"; response: LoadMoreResponseLike }
  /** The append attempt's own fetch threw (network/timeout/etc.), never
   *  reaching a structured response. */
  | { kind: "append_error" }
  /** The result of the recovery's own silent page-1 reload. */
  | { kind: "recovery_reload_result"; response: LoadMoreResponseLike }
  /** The recovery's page-1 reload fetch itself threw. */
  | { kind: "recovery_reload_error" };

export type ManualExitReason = "stale_cursor" | "warmup_timeout" | "append_failed" | "network_error";

export type LoadMoreTransition =
  /** Apply the response normally — nothing special to decide. */
  | { type: "success" }
  /** Poll the SAME cursor again after delayMs. */
  | { type: "poll"; delayMs: number; nextContext: LoadMoreActionContext }
  /** Silently reload page 1 before retrying the append once. */
  | { type: "recover_page_one"; nextContext: LoadMoreActionContext }
  /** The recovery's reload produced a fresh cursor with more to load —
   *  retry the original append against it, exactly once. */
  | { type: "append_fresh"; cursor: string; nextContext: LoadMoreActionContext }
  /** Stop silently retrying; the caller must show an honest manual exit
   *  and leave every existing result/filter/selection/scroll untouched. */
  | { type: "manual_exit"; reason: ManualExitReason };

export function decideNextLoadMoreStep(
  event: LoadMoreEvent,
  context: LoadMoreActionContext,
  now: number,
): LoadMoreTransition {
  switch (event.kind) {
    case "append_error":
    case "recovery_reload_error":
      // A raw network/timeout failure never triggers a recovery attempt —
      // recovery is specifically for a STRUCTURED stale-cursor response,
      // not "the fetch itself failed."
      return { type: "manual_exit", reason: "network_error" };

    case "recovery_reload_result": {
      const { response } = event;
      if (!response.success) {
        return { type: "manual_exit", reason: "network_error" };
      }
      if (response.hasMore && response.nextCursor) {
        return {
          type: "append_fresh",
          cursor: response.nextCursor,
          // Fresh cursor starts its own polling episode from 0 — the
          // OLD cursor's poll history says nothing about how quickly the
          // NEW one might warm up. recoveryCount and deadlineAt are
          // untouched: this action has already spent its one recovery,
          // and its total wait budget keeps counting from when the
          // action itself started, not from this reload.
          nextContext: { ...context, warmupPollAttempt: 0 },
        };
      }
      // Page 1 is fresh and honestly has nothing more to append — not a
      // failure, just nothing left to recover into.
      return { type: "success" };
    }

    case "append_result": {
      const { response } = event;

      if (!response.success) {
        if (isStaleCursorError(response.error)) {
          if (context.recoveryCount < MAX_RECOVERY_COUNT_PER_ACTION) {
            return {
              type: "recover_page_one",
              nextContext: { ...context, recoveryCount: context.recoveryCount + 1 },
            };
          }
          // This action already spent its one automatic recovery (e.g. a
          // warmup poll against the recovery's own fresh cursor just came
          // back stale too) — never recover a second time.
          return { type: "manual_exit", reason: "stale_cursor" };
        }
        return { type: "manual_exit", reason: "append_failed" };
      }

      if (response.inventory_warming && (response.itemsCount ?? 0) === 0) {
        if (hasWarmupBudgetRemaining(context, now)) {
          return {
            type: "poll",
            delayMs: computeWarmupPollDelayMs(context.warmupPollAttempt),
            nextContext: { ...context, warmupPollAttempt: context.warmupPollAttempt + 1 },
          };
        }
        return { type: "manual_exit", reason: "warmup_timeout" };
      }

      return { type: "success" };
    }
  }
}
